import React, { FC, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import classNames from 'classnames';

import { NgCard, NgCardContent, NgRailItem, SkeletonLoader } from './index';
import { hasAccessByRole } from '../helpers';

export interface CardRailMenuProps {
  grants?: UserGrants;
  isLoading?: boolean;
  items: ProjectSideNavItem[];
}

const StyledList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;

  .skeleton-grid {
    display: flex;
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.medium};
  }
`;

export const CardRailMenu: FC<CardRailMenuProps> = ({ items, grants, isLoading }) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const handleNavItemClick = useCallback((url: string) => {
    navigate(url);
  }, []);

  return (
    <NgCard>
      <NgCardContent>
        <StyledList>
          {isLoading ? (
            <div className="skeleton-grid">
              <SkeletonLoader count={1} single width="100%" height={36} />
              <SkeletonLoader count={1} single width="100%" height={36} />
              <SkeletonLoader count={1} single width="100%" height={36} />
            </div>
          ) : (
            (items || []).map((item, index) => {
              if (
                item.accessRoles?.length &&
                !hasAccessByRole({
                  allowedRoles: item?.accessRoles,
                  role: grants?.role,
                })
              ) {
                return null;
              }

              const {
                submenu: { component: Component, accessRoles: submenuAccessRoles } = {},
                slug: itemSlug,
                url,
                displayName,
              } = item;

              if (
                Component &&
                submenuAccessRoles?.length &&
                hasAccessByRole({
                  allowedRoles: submenuAccessRoles,
                  role: grants?.role,
                })
              ) {
                return (
                  <React.Fragment key={index}>
                    <NgRailItem
                      title={displayName}
                      className={classNames({ active: pathname === url || pathname.startsWith(url + '/') })}
                      onClick={() => handleNavItemClick(url)}
                    />
                    {Component}
                  </React.Fragment>
                );
              }

              return (
                <NgRailItem
                  key={index}
                  title={displayName}
                  className={classNames({ active: pathname === url || pathname.startsWith(url + '/') })}
                  onClick={() => handleNavItemClick(url)}
                />
              );
            })
          )}
        </StyledList>
      </NgCardContent>
    </NgCard>
  );
};
